@if (!loading() || useOptionClicked()) {
  <div class="flex w-full mb-5 gap-10">
    <mat-form-field class="custom-form w-full">
      <app-icon src="/icons/search.svg" matPrefix alt="Search" class="w-4 h-4" />
      <input matInput [formControl]="searchControl" [placeholder]="'shared.search' | translate" />
    </mat-form-field>

    <button mat-flat-button data-testid="add-channel-button" (click)="onAddChannelClick()">
      <div class="flex items-center gap-2 w-max">
        <app-icon src="/icons/plus.svg" alt="plus" [size]="20" />
        {{ 'pages.creatorDetails.addPlatform.addChannel' | translate }}
      </div>
    </button>
  </div>
} @else {
  <div class="flex w-full mb-5">
    <ngx-skeleton-loader class="w-full h-12" [theme]="{ height: '48px', width: '100%', 'border-radius': '100vw' }" />
  </div>
}

<div class="table-container simple-hover-table relative">
  @if (loadedChannels() || useOptionClicked()) {
    <table mat-table [dataSource]="dataSource()">
      <!-- Platform Column -->
      <ng-container matColumnDef="platform">
        <th mat-header-cell *matHeaderCellDef>{{ 'shared.platform' | translate }}</th>
        <td mat-cell *matCellDef="let channel">
          <div class="flex items-center gap-2">
            <app-social-badge
              backgroundColor="transparent"
              [type]="channel.platform"
              [size]="24"
              [iconSize]="24"
              [isLive]="channel.isLive"
              [borderRadius]="100"
            />
            <span class="font-bold text-sm">{{ 'shared.platformFeatures.' + channel.platform | translate }}</span>
          </div>
        </td>
      </ng-container>

      <!-- Channel Column -->
      <ng-container matColumnDef="channel">
        <th mat-header-cell *matHeaderCellDef>{{ 'shared.channel' | translate }}</th>
        <td mat-cell *matCellDef="let channel; let index = index">
          <div class="flex items-center gap-2">
            <app-icon-badge
              backgroundColor="transparent"
              [content]="channel.iconUrl"
              [fallbackText]="channel.platformCreatorId | removeAtSign"
              [fallbackBackgroundColor]="index % 2 === 0 ? 'var(--shikenso-base-white)' : 'var(--shikenso-gray-base)'"
              [size]="24"
              [iconSize]="24"
              [fontSize]="14"
              [borderRadius]="100"
            />
            <span class="text-sm" data-testid="creator-platform-channel-handle">{{
              channel.platformCreatorId | removeAtSign
            }}</span>
          </div>
        </td>
      </ng-container>

      <!-- Authentication Column -->
      @if (hasCreatorAuthenticatePermission()) {
        <ng-container matColumnDef="authentication">
          <th mat-header-cell *matHeaderCellDef>{{ 'shared.authentication' | translate }}</th>
          <td mat-cell *matCellDef="let channel; let index = index">
            @let isAuthRequired = channel.platform === EPlatform.instagram || channel.platform === EPlatform.facebook;

            <div
              class="flex items-center justify-center px-3 py-1 rounded-full w-fit"
              [ngClass]="{
                'bg-shikenso-success-light text-shikenso-success-base': channel.authenticated && isAuthRequired,
                'bg-shikenso-error-light text-shikenso-error-base': !channel.authenticated && isAuthRequired,
                'bg-shikenso-base-white': !isAuthRequired && index % 2 === 0,
                'bg-shikenso-gray-base': !isAuthRequired && index % 2 !== 0,
              }"
            >
              <span class="text-xs font-bold leading-4">
                {{
                  !isAuthRequired
                    ? ('shared.notRequired' | translate)
                    : channel.authenticated
                      ? ('shared.authenticated' | translate)
                      : ('shared.notAuthenticated' | translate)
                }}
              </span>
            </div>
          </td>
        </ng-container>
      }

      <!-- Followers Column -->
      @if (hasCreatorFollowersReadPermission()) {
        <ng-container matColumnDef="followers">
          <th mat-header-cell *matHeaderCellDef>
            {{ 'shared.followers' | translate }} ({{ 'shared.total' | translate }}
            {{ totalFollowers() | number: '1.0-0' }})
          </th>
          <td mat-cell *matCellDef="let channel">
            <div class="flex items-center">
              <span>{{ channel.followers | number: '1.0-0' }}</span>
            </div>
          </td>
        </ng-container>
      }

      <!-- Hover Actions Column -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let channel">
          <div *hasPermission="[EPermissions.creatorUpdateOwn, EPermissions.creatorUpdateSelf]">
            <button
              mat-icon-button
              color="secondary"
              class="mat-secondary-transparent-button-color"
              data-testid="creator-platform-channel-actions-trigger"
              [matMenuTriggerFor]="menu"
              (click)="$event.stopPropagation()"
              #menuTrigger="matMenuTrigger"
            >
              <app-icon src="/icons/three-dots.svg" alt="More" class="!w-4 !h-4" />
            </button>

            <mat-menu #menu="matMenu">
              @if (isLoading()) {
                <div class="flex items-center justify-center">
                  <app-dots-spinner />
                </div>
              } @else {
                @let isAuthRequired =
                  channel.platform === EPlatform.instagram || channel.platform === EPlatform.facebook;

                @if (instagramAuthUrl() && isAuthRequired && hasCreatorAuthenticatePermission()) {
                  <button
                    *hasPermission="[EPermissions.creatorUpdateOwn]"
                    mat-menu-item
                    matTooltipPosition="above"
                    data-testid="creator-platform-authenticate"
                    (click)="authenticateClicked($event, menuTrigger)"
                  >
                    <div class="flex items-center gap-2">
                      {{ 'shared.authenticate' | translate }}
                      <app-icon src="/icons/circle-arrow-up-right.svg" alt="Next" [size]="16" />
                    </div>
                  </button>

                  <button
                    *hasPermission="[EPermissions.creatorUpdateOwn]"
                    mat-menu-item
                    matTooltipPosition="above"
                    data-testid="creator-platform-authenticate-guide"
                    (click)="authenticateGuideClicked($event, menuTrigger)"
                  >
                    <div class="flex items-center gap-2">
                      {{ 'shared.authenticateGuide' | translate }}
                      <app-icon src="/icons/circle-arrow-up-right.svg" alt="Next" [size]="16" />
                    </div>
                  </button>
                }

                <button
                  *hasPermission="[EPermissions.creatorUpdateOwn]"
                  mat-menu-item
                  matTooltipPosition="above"
                  data-testid="creator-platform-use-as-default"
                  [disabled]="!channel.iconUrl"
                  [matTooltip]="'pages.channels.datatable.channelHasNoImagePleaseRefresh' | translate"
                  [matTooltipDisabled]="channel.iconUrl"
                  (click)="onUseAsDefaultClick($event, channel, menuTrigger)"
                >
                  {{ 'shared.form.useAsDefault' | translate }}
                </button>

                <button
                  *hasPermission="[EPermissions.creatorUpdateOwn]"
                  mat-menu-item
                  matTooltipPosition="above"
                  data-testid="creator-platform-use-image"
                  [disabled]="!channel.iconUrl"
                  [matTooltip]="'pages.channels.datatable.channelHasNoImagePleaseRefresh' | translate"
                  [matTooltipDisabled]="channel.iconUrl"
                  (click)="onUseImageClick($event, channel, menuTrigger)"
                >
                  {{ 'shared.form.useImage' | translate }}
                </button>

                <button
                  *hasPermission="[EPermissions.creatorUpdateOwn, EPermissions.creatorUpdateSelf]"
                  mat-menu-item
                  data-testid="creator-platform-refresh-image"
                  (click)="onRefreshImageClick($event, channel, menuTrigger)"
                >
                  {{ 'shared.form.refreshImage' | translate }}
                </button>

                <button
                  *hasPermission="[EPermissions.creatorDeleteOwn, EPermissions.creatorUpdateSelf]"
                  mat-menu-item
                  data-testid="creator-platform-delete-channel"
                  (click)="onDeleteClick($event, channel, menuTrigger)"
                >
                  {{ 'shared.form.delete' | translate }}
                </button>
              }
            </mat-menu>
          </div>
        </td>
      </ng-container>
      <tr mat-header-row *matHeaderRowDef="displayedColumns()" class="header-row"></tr>
      <tr
        mat-row
        *matRowDef="let row; let index = index; columns: displayedColumns()"
        class="data-row"
        (click)="onRowClick(row)"
      ></tr>
    </table>
  } @else {
    <app-skeleton-loader
      containerClass="platforms-container"
      headerPadding="0 16px 4px 16px"
      headerMargin="4px 0 4px 0"
      [columns]="skeletonPlatformColumns()"
      [rows]="skeletonPlatformRows()"
      [showPaginator]="false"
    />
  }
</div>
